import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { IAppState } from '@mafc/common';
import { Drawer } from '@mafc/trolly';

import { StyledDrawerTitle } from 'common/styled/Drawer.style';
import useOrderSummary from 'common/OrderSummary/hooks/useOrderSummary.hook';
import { getFormattedPrice, getFormattedPriceInUSD, prettifyPrice } from 'utils/price.util';
import { setOrderTotalCurrency } from 'common/OrderSummary/redux/orderSummary.action';
import { CurrencyCodes } from 'pages/Checkout/constants/checkout.constant';
import TrackerCheckout from 'utils/Tracker.checkout';
import * as S from './ChangeCurrencyDrawer.styles';

const ChangeCurrencyDrawer: React.FC<{
  isOpen: boolean;
  closeDrawer: () => void;
}> = ({ isOpen, closeDrawer }) => {
  const { t } = useTranslation();
  const currencyRate = useSelector(
    (state: IAppState) => state.appConfig && state.appConfig.currencyRate
  );
  const dispatch = useDispatch();

  const [selectedValue, setSelectedValue] = useState(CurrencyCodes.USD);

  const { totalPriceWithTax } = useOrderSummary();

  const priceInMainCurrency = getFormattedPrice(totalPriceWithTax, false, true, true);

  const priceInUSD = getFormattedPriceInUSD(totalPriceWithTax, currencyRate);

  const handleContinueClick = () => {
    TrackerCheckout.changeCurrencyContinueClicked(selectedValue);
    dispatch(
      setOrderTotalCurrency({
        orderTotalCurrency: selectedValue,
      })
    );
    closeDrawer();
  };

  const handleCancelClick = () => {
    TrackerCheckout.changeCurrencyCancelClicked();
    closeDrawer();
  };

  const conversionRate = prettifyPrice(1 / currencyRate);

  return (
    <Drawer
      anchor={['bottom', null, 'right']}
      isOpen={isOpen}
      onClose={closeDrawer}
      title={<StyledDrawerTitle>{t('changeCurrency.changeCurrencyHeader')}</StyledDrawerTitle>}
      variant="float"
      containerStyles={{
        borderTopLeftRadius: [4, null, null, 0],
        borderTopRightRadius: [4, null, null, 0],
      }}
      isTitleBorder={false}
      contentStyles={{ pt: 1, borderRadius: '20px' }}
      width={[null, null, '480px']}
    >
      <S.CurrencyList>
        <S.CustomLabel>
          <S.CustomRadio
            type="radio"
            value={CurrencyCodes.LBP}
            checked={selectedValue === CurrencyCodes.LBP}
            onChange={setSelectedValue.bind(null, CurrencyCodes.LBP)}
          />
          <S.Currency selected={selectedValue === CurrencyCodes.LBP}>
            {priceInMainCurrency}
          </S.Currency>
        </S.CustomLabel>

        <S.CustomLabel>
          <S.CustomRadio
            type="radio"
            value={CurrencyCodes.USD}
            checked={selectedValue === CurrencyCodes.USD}
            onChange={setSelectedValue.bind(null, CurrencyCodes.USD)}
          />
          <S.StyledLabel>
            <S.Currency selected={selectedValue === CurrencyCodes.USD}>{priceInUSD}</S.Currency>
            <S.ConversionRate>
              {t('changeCurrency.conversionRate', { rate: conversionRate })}
            </S.ConversionRate>
          </S.StyledLabel>
        </S.CustomLabel>
      </S.CurrencyList>

      <S.Actions>
        <S.Cancel
          variant="naked"
          width={[1, null, 1 / 2]}
          onClick={handleCancelClick}
          data-testid="change-currency-cancel-btn"
        >
          {t('changeCurrency.cancel')}
        </S.Cancel>

        <S.Continue
          variant="secondary"
          width={[1, null, 1 / 2]}
          onClick={handleContinueClick}
          data-testid="change-currency-submit-btn"
        >
          {t('changeCurrency.continue')}
        </S.Continue>
      </S.Actions>
    </Drawer>
  );
};

export default ChangeCurrencyDrawer;
